import React, { useState, useEffect, useRef } from 'react';
import './LevelModal.css';
import Sparkline from './Sparkline';

/**
 * Level Modal Component
 * Displays leaderboard, user stats, and point breakdown
 */
const LevelModal = ({ isOpen, onClose, userLevelData, leaderboardData, pointsHistory }) => {
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [sparklineDays, setSparklineDays] = useState(7);
  const modalRef = useRef(null);
  const leaderboardRef = useRef(null);

  useEffect(() => {
    if (isOpen && leaderboardRef.current && userLevelData) {
      // Auto-scroll to current user's position
      const currentUserRow = leaderboardRef.current.querySelector('.current-user-row');
      if (currentUserRow) {
        currentUserRow.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
  }, [isOpen, userLevelData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getSparklineData = (user) => {
    if (!user.sparkline) return [];
    
    const days = sparklineDays === 7 ? 7 : 30;
    return user.sparkline.slice(-days);
  };

  const getSparklineColor = (user, index) => {
    if (user.is_current_user) return '#FFD700';
    if (index < 3) return '#00D0FF';
    return '#666';
  };

  return (
    <div className="level-modal-overlay">
      <div className="level-modal" ref={modalRef}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">Level System</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button 
            className={`tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('leaderboard')}
          >
            Leaderboard
          </button>
          <button 
            className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Your Stats
          </button>
          <button 
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {activeTab === 'leaderboard' && (
            <div className="leaderboard-section">
              <div className="leaderboard-header">
                <h3>Global Leaderboard</h3>
                <div className="sparkline-controls">
                  <button 
                    className={`sparkline-btn ${sparklineDays === 7 ? 'active' : ''}`}
                    onClick={() => setSparklineDays(7)}
                  >
                    7D
                  </button>
                  <button 
                    className={`sparkline-btn ${sparklineDays === 30 ? 'active' : ''}`}
                    onClick={() => setSparklineDays(30)}
                  >
                    30D
                  </button>
                </div>
              </div>

              <div className="leaderboard-table" ref={leaderboardRef}>
                <div className="leaderboard-header-row">
                  <div className="col-rank">#</div>
                  <div className="col-user">User</div>
                  <div className="col-level">Level</div>
                  <div className="col-points">Points</div>
                  <div className="col-sparkline">Trend</div>
                </div>

                {leaderboardData?.map((user, index) => (
                  <div 
                    key={user.user_id} 
                    className={`leaderboard-row ${user.is_current_user ? 'current-user-row' : ''}`}
                  >
                    <div className="col-rank">
                      <span className={`rank-number ${index < 3 ? 'top-three' : ''}`}>
                        {user.rank}
                      </span>
                    </div>
                    <div className="col-user">
                      <img 
                        src={`/assets/Profile/${user.avatar}`} 
                        alt={user.name}
                        className="user-avatar"
                        onError={(e) => {
                          e.target.src = '/assets/Profile/boy-1.svg';
                        }}
                      />
                      <span className="user-name">{user.name}</span>
                    </div>
                    <div className="col-level">
                      <span className="level-badge">Lv.{user.level}</span>
                    </div>
                    <div className="col-points">
                      <span className="points-value">{user.total_points.toLocaleString()}</span>
                    </div>
                    <div className="col-sparkline">
                      <Sparkline 
                        data={getSparklineData(user)}
                        color={getSparklineColor(user, index)}
                        width={60}
                        height={30}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'stats' && userLevelData && userLevelData.level_data && (
            <div className="stats-section">
              <div className="user-level-card">
                <div className="level-badge-large">
                  <div className="trophy-icon-large">
                    <span className="trophy-rank">{userLevelData.level_data.level}</span>
                  </div>
                  <div className="level-info-large">
                    <h3>Level {userLevelData.level_data.level}</h3>
                    <p>{userLevelData.level_data.level_name || 'Beginner'}</p>
                  </div>
                </div>
                
                <div className="points-summary">
                  <div className="points-item">
                    <span className="points-label">Total Points</span>
                    <span className="points-value">
                      {userLevelData.level_data.total_points?.toLocaleString() || '0'}
                    </span>
                  </div>
                  <div className="points-item">
                    <span className="points-label">Points This Level</span>
                    <span className="points-value">
                      {userLevelData.level_data.points_this_level?.toLocaleString() || '0'}
                    </span>
                  </div>
                  <div className="points-item">
                    <span className="points-label">Points to Next</span>
                    <span className="points-value">
                      {userLevelData.level_data.points_to_next?.toLocaleString() || '0'}
                    </span>
                  </div>
                </div>

                {userLevelData.level_data.estimated_days_to_next && (
                  <div className="time-estimate">
                    <span className="estimate-label">Estimated time to next level:</span>
                    <span className="estimate-value">{userLevelData.level_data.estimated_days_to_next} days</span>
                  </div>
                )}
              </div>

              {userLevelData.level_data.points_breakdown && (
                <div className="points-breakdown">
                  <h4>Points Breakdown (Last 30 Days)</h4>
                  <div className="breakdown-list">
                    {userLevelData.level_data.points_breakdown.map((item, index) => (
                      <div key={index} className="breakdown-item">
                        <span className="breakdown-activity">{item.activity_type.replace('_', ' ')}</span>
                        <span className="breakdown-points">{item.total_points} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Add fallback for missing data */}
          {activeTab === 'stats' && (!userLevelData || !userLevelData.level_data) && (
            <div className="stats-section">
              <div className="loading-spinner">Loading your stats...</div>
            </div>
          )}

          {activeTab === 'history' && pointsHistory && (
            <div className="history-section">
              <h3>Recent Activity</h3>
              <div className="history-list">
                {pointsHistory.map((entry, index) => (
                  <div key={index} className="history-item">
                    <div className="history-date">
                      {formatDate(entry.created_at)}
                    </div>
                    <div className="history-activity">
                      {entry.activity_name}
                    </div>
                    <div className="history-description">
                      {entry.description}
                    </div>
                    <div className="history-points">
                      +{entry.points_earned}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LevelModal;
